import { useSelector } from "react-redux";
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { RootState } from "../../../redux/store";
import { menuRoutes } from "../../../router/menu";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useCallback, useEffect, useState } from "react";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import PageHeader from "../../../layout/shared/page-header";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { Button, Loader, Tooltip } from "rizzui";
import { setSelectedView } from "../../../redux/issuesProfileSlice";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import CarrionFormModal from "../components/CarrionFormModal";
import { CarrionService } from "../../../services/carrion/carrionService";
import useCarrionStatus from "../../../hooks/api-calls/useCarrionStatus";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.carrion.profile.info,
        permission: {
            group: 'carrion',
            action: 'get',
        }
    },
    {
        label: 'Fotografias',
        path: menuRoutes.carrion.profile.photos,
        permission: {
            group: 'carrion_photos',
            action: 'get',
        }
    }

];


export default function CarrionProfileLayout({ children, isLoading, setRefetchTab }: { children?: React.ReactNode, isLoading?: boolean, setRefetchTab?: (state:boolean) => void }) {

    // STATES

    const [ carrion, setCarrion ] = useState<any>(null);
    const [isOpen, setIsOpen] = useState(false);


    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.issuesProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const service = new CarrionService();
    const { getCarrionStatusList } = useCarrionStatus();


    // DATA FETCHING
    
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getCarrionById(id);
        return response.getResponseData();
    }, [id]));

    const pageHeader = {
        title: `Carroña ${data?.carrionId || 'Carroña'}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Carroñas' },
            { name: 'Datos de la carroña' },
        ],
    }


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR CARROÑA
     * @EN DELETE CARRION
     * @param id
     * 
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteCarrions(id)).getResponseData();
            toast.success(response.message);
            navigate(menuRoutes.issues.path);
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR EL MODAL
     * @EN CLOSE THE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose = () => {
        setIsOpen(false);

        setTimeout(() => {
            setCarrion(null);
            refetch();
        }, 150);
    }
    //-----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO LA CARROÑA
     * @EN CHANGE THE STATUS OF THE CARRION
     * 
     * @param id
     * @param status
     */ 
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusCarrion(id, status)).getResponseData();
        if(response.success) {
            toast.success("Estado de la carroña cambiada correctamente");
            navigate(menuRoutes.carrion.path);
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.carrion.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
            <>
                <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb} className="mb-0 lg:mb-0">
                    <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                        { userCan('edit', 'carrion')&& data && (
                            <>
                                <div className='flex flex-col gap-2 justify-content-center text-center mt-1' key={id + data.carrionStatus?.id}>
                                        {(id && data.carrionStatus?.id) ? (
                                            <StatusDropdown
                                                entityId={id}
                                                title="¿Estas seguro de cambiar el estado de la carroña?"
                                                statesOptions={getCarrionStatusList()}
                                                currentState={data.carrionStatus}
                                                handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(id, statusId)}
                                                hasComments={false}
                                            />
                                        ) : "N/A"}
                                </div>
                                <Tooltip content={'Editar carroña'} placement={'top'} color={'invert'} size={'sm'}>
                                    <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                        setIsOpen(true);
                                        setCarrion(data.id);
                                    }} className="bg-white">
                                        Editar
                                    </Button>
                                </Tooltip>
                             </>
                        )}
                        {userCan('delete', 'carrion') && (
                            <DeletePopover
                                title={`Eliminar Carroña`}
                                description={`¿Estás seguro de que deseas eliminar la carroña?`}
                                size={'md'}
                                onDelete={() => handleDelete(id)}
                                actionIconClassName='h-10 w-10'
                            />
                        )}

                    </div>
                </PageHeader>
    
                <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />
    
                {isLoading ? <Loader height='60vh' /> : <Outlet />}

                <CarrionFormModal carrion={carrion} isOpen={isOpen} onClose={onClose} refetch={refetch} />
            </>
        );
}